import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSoftDelete: () => Promise<void>;
  onHardDelete: () => Promise<void>;
  userEmail: string;
}

export function DeleteUserModal({
  isOpen,
  onClose,
  onSoftDelete,
  onHardDelete,
  userEmail,
}: DeleteUserModalProps) {
  const theme = useTheme();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailMatches = confirmEmail === userEmail;

  async function handleSoftDelete() {
    setLoading(true);
    setError(null);
    try {
      await onSoftDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable user');
    } finally {
      setLoading(false);
    }
  }

  async function handleHardDelete() {
    if (!emailMatches) return;
    setLoading(true);
    setError(null);
    try {
      await onHardDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={
        // v8 ignore next
        (open) => {
          if (!open) onClose();
        }
      }
    >
      <Dialog.Portal>
        <Dialog.Overlay
          css={css`
            position: fixed;
            inset: 0;
            background: ${theme.colors.overlay};
            z-index: 50;
          `}
        />
        <Dialog.Content
          css={css`
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.lg};
            box-shadow: ${shadows.prominent};
            padding: ${spacing.xl};
            width: 90vw;
            max-width: 480px;
            z-index: 51;
            display: flex;
            flex-direction: column;
            gap: ${spacing.md};
          `}
        >
          <Dialog.Title
            css={css`
              margin: 0;
              font-size: ${fontSizes.xl};
              color: ${theme.colors.text};
              font-weight: 700;
            `}
          >
            Delete User
          </Dialog.Title>

          <Dialog.Description
            css={css`
              margin: 0;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
            `}
          >
            {userEmail}
          </Dialog.Description>

          {error && (
            <p
              css={css`
                margin: 0;
                color: ${theme.colors.error};
                font-size: ${fontSizes.sm};
              `}
            >
              {error}
            </p>
          )}

          <div
            css={css`
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.md};
              padding: ${spacing.md};
            `}
          >
            <h3
              css={css`
                margin: 0 0 ${spacing.xs};
                font-size: ${fontSizes.md};
                color: ${theme.colors.text};
              `}
            >
              Soft Delete
            </h3>
            <p
              css={css`
                margin: 0 0 ${spacing.sm};
                font-size: ${fontSizes.sm};
                color: ${theme.colors.textSecondary};
              `}
            >
              Disable the account. User loses access but data is preserved for audit.
            </p>
            <button
              type="button"
              onClick={handleSoftDelete}
              disabled={loading}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
                padding: ${spacing.sm} ${spacing.md};
                border: 1px solid ${theme.colors.border};
                border-radius: ${radii.sm};
                background: ${theme.colors.surface};
                color: ${theme.colors.text};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              {loading && <Spinner size="sm" aria-hidden />}
              Disable Account
            </button>
          </div>

          <div
            css={css`
              border: 1px solid ${theme.colors.error};
              border-radius: ${radii.md};
              padding: ${spacing.md};
            `}
          >
            <h3
              css={css`
                margin: 0 0 ${spacing.xs};
                font-size: ${fontSizes.md};
                color: ${theme.colors.error};
              `}
            >
              Permanent Delete
            </h3>
            <p
              css={css`
                margin: 0 0 ${spacing.sm};
                font-size: ${fontSizes.sm};
                color: ${theme.colors.textSecondary};
              `}
            >
              Permanently remove user from Stripe, Clerk, and the database. This cannot be undone.
            </p>
            <p
              css={css`
                margin: 0 0 ${spacing.sm};
                font-size: ${fontSizes.sm};
                color: ${theme.colors.text};
              `}
            >
              Type <strong>{userEmail}</strong> to confirm:
            </p>
            <input
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={userEmail}
              css={css`
                width: 100%;
                padding: ${spacing.sm} ${spacing.md};
                border: 1px solid ${theme.colors.border};
                border-radius: ${radii.sm};
                background: ${theme.colors.background};
                color: ${theme.colors.text};
                font-size: ${fontSizes.sm};
                margin-bottom: ${spacing.sm};
                box-sizing: border-box;
              `}
            />
            <button
              type="button"
              onClick={handleHardDelete}
              disabled={!emailMatches || loading}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
                padding: ${spacing.sm} ${spacing.md};
                border: none;
                border-radius: ${radii.sm};
                background: ${theme.colors.error};
                color: ${theme.colors.text};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                font-weight: 600;
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              {loading && <Spinner size="sm" aria-hidden />}
              Permanently Delete
            </button>
          </div>

          <div
            css={css`
              display: flex;
              justify-content: flex-end;
            `}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              css={css`
                padding: ${spacing.sm} ${spacing.md};
                border: 1px solid ${theme.colors.border};
                border-radius: ${radii.sm};
                background: ${theme.colors.surface};
                color: ${theme.colors.text};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              Cancel
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
